import { DateTime } from "luxon";
import { useMemo } from "react";
import { MultiColumnCalendarGrid } from "@/domains/appointment/appointments/components/calendar/CalendarGrid";
import { OperatingHoursOverlay } from "@/domains/appointment/appointments/components/calendar/operating-hours-overlay";
import {
  createTimeSlotConfig,
  getCalendarThemeConfig,
  getTimeColumnWidth,
} from "@/domains/appointment/appointments/config/calendar-config";
import {
  calculateAppointmentPosition,
  calculateExtendedTimeRange,
  computeAppointmentLayoutMap,
  isToday,
  type PositionConfig,
} from "@/domains/appointment/appointments/utils/calendar-utils";
import { CalendarAppointmentCard } from "@/domains/appointment/calendar/components/calendar-appointment-card";
import { useOperatingHours } from "@/domains/settings/hooks/use-operating-hours";
import type { AppointmentBookingStatus } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { timezoneUtils } from "@/lib/timezone";
import { useCalendarAppointments } from "../../hooks/use-calendar-appointments";
import { useCalendarFilters } from "../../hooks/use-calendar-filters";
import { useCalendarStateStore } from "../../store/use-calendar-state-store";
import type { AppointmentListFragment } from "../../types";
import { CurrentTimeLine } from "../ui/current-time-line";

type OperatingHoursSlot = {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  slotOrder: number;
};

// Utility function to get operating hours for a specific date (non-hook version)
function getOperatingHoursForDate(
  operatingHours: OperatingHoursSlot[],
  date: DateTime
): OperatingHoursSlot[] {
  const dayOfWeek = date.weekday % 7; // Convert Luxon weekday (1-7) to (0-6)
  return operatingHours.filter((hour) => hour.dayOfWeek === dayOfWeek);
}

interface WeekViewProps {
  currentDate: DateTime;
  onDateChange?: (date: DateTime) => void;
  onAppointmentClick?: (appointmentId: string) => void;
  onAppointmentDrop?: (
    appointmentId: string,
    newDate: DateTime,
    newWorkerId?: string | null
  ) => void;
}

export function WeekView({ currentDate, onAppointmentClick }: WeekViewProps) {
  const { currentSalon } = useSalonStore();
  const { statusFilters, workerFilters } = useCalendarFilters();
  const { timeScale } = useCalendarStateStore();

  // Fetch operating hours
  const { operatingHours } = useOperatingHours(currentSalon?.id);

  // Calculate week start (Sunday)
  const weekStart = currentDate.startOf("week");

  // Generate 7 days for the week
  const weekDays = useMemo(() => {
    const days: DateTime[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(weekStart.plus({ days: i }));
    }
    return days;
  }, [weekStart]);

  // Fetch appointments for the entire week
  const { appointments } = useCalendarAppointments({
    startDate: weekStart.startOf("week"),
    endDate: weekStart.endOf("week"),
    statusFilters: statusFilters as AppointmentBookingStatus[],
    workerFilters,
  });

  // Calculate extended time range based on operating hours AND appointments from all 7 days
  const { startHour, endHour } = useMemo(() => {
    let allOperatingHours: Array<{ openTime?: string; closeTime?: string }> = [];

    weekDays.forEach((date) => {
      const dayHours = getOperatingHoursForDate(operatingHours || [], date);
      allOperatingHours = [
        ...allOperatingHours,
        ...dayHours.map((h) => ({ openTime: h.startTime, closeTime: h.endTime })),
      ];
    });

    return calculateExtendedTimeRange(allOperatingHours, appointments);
  }, [operatingHours, weekDays, appointments]);

  // Configuration for time slots and positioning using dynamic scale
  const scaleConfig = getCalendarThemeConfig(timeScale);
  const safeStartHour = !Number.isNaN(startHour) ? startHour : 0;
  const safeEndHour = !Number.isNaN(endHour) ? endHour : 24;
  const timeConfig = createTimeSlotConfig(safeStartHour, safeEndHour, {
    hourHeight: scaleConfig.layout.hourHeight,
  });

  const positionConfig: PositionConfig = useMemo(
    () => ({
      hourHeight: scaleConfig.layout.hourHeight,
      minHeight: scaleConfig.layout.minAppointmentHeight,
    }),
    [scaleConfig.layout.hourHeight, scaleConfig.layout.minAppointmentHeight]
  );

  // Group appointments by date and calculate positions with new global overlap algorithm
  const appointmentsByDate = useMemo(() => {
    const groups: Record<
      string,
      Array<{
        appointment: AppointmentListFragment;
        style: React.CSSProperties;
        hasOverlap: boolean;
      }>
    > = {};

    weekDays.forEach((date) => {
      const dateKey = date.toISODate() ?? "";

      const dayAppointments = appointments.filter((appointment) => {
        const appointmentDate = timezoneUtils.fromISO(appointment.startTime);
        return appointmentDate.hasSame(date, "day");
      });

      // Use new global layout algorithm
      const layoutMap = computeAppointmentLayoutMap(dayAppointments, positionConfig, safeStartHour);

      groups[dateKey] = dayAppointments.map((appointment) => {
        const position = calculateAppointmentPosition(appointment, positionConfig, safeStartHour);
        const layout = layoutMap.get(appointment.id) ?? {
          width: "calc(100% - 6px)",
          left: "3px",
          hasOverlap: false,
          overlapCount: 1,
          zIndex: 1,
        };

        return {
          appointment,
          style: {
            position: "absolute" as const,
            top: `${position.top}px`,
            height: `${Math.max(position.height, positionConfig.minHeight || 24)}px`,
            left: layout.left,
            width: layout.width,
            zIndex: layout.zIndex,
          },
          hasOverlap: layout.hasOverlap,
        };
      });
    });

    return groups;
  }, [appointments, weekDays, positionConfig, safeStartHour]);

  const handleAppointmentClick = (appointment: AppointmentListFragment) => {
    onAppointmentClick?.(appointment.id);
  };

  const renderDayColumn = (date: DateTime, dayIndex: number) => {
    const dateKey = date.toISODate() ?? "";
    const dayAppointments = appointmentsByDate[dateKey] || [];
    const showCurrentTimeLine = isToday(date);

    return (
      <div
        key={dayIndex}
        className="flex-1 min-w-[280px] sm:min-w-0 border-r border-border last:border-r-0"
      >
        {/* Time Slots Column */}
        <div
          className="relative"
          style={{
            minHeight: `${(safeEndHour - safeStartHour) * scaleConfig.layout.hourHeight}px`,
          }}
        >
          {/* Hour Dividers */}
          {Array.from({ length: safeEndHour - safeStartHour }).map((_, hour) => (
            <div
              key={`hour-${hour}`}
              className="border-b border-border last:border-b-0"
              style={{ height: `${scaleConfig.layout.hourHeight}px` }}
            />
          ))}
          {/* 15-minute interval lines */}
          {Array.from({ length: (safeEndHour - safeStartHour) * 4 }).map((_, interval) => {
            // Skip lines that coincide with hour lines (every 4th interval)
            if (interval % 4 === 0) return null;
            const topPx = interval * (scaleConfig.layout.hourHeight / 4);

            return (
              <div
                key={`interval-top-${topPx}`}
                className="absolute left-0 right-0 border-b border-border/50 pointer-events-none"
                style={{
                  top: `${topPx}px`,
                  zIndex: 1,
                }}
              />
            );
          })}

          {/* Operating Hours Overlay */}
          <OperatingHoursOverlay
            operatingHours={getOperatingHoursForDate(operatingHours || [], date)}
            startTime={date.startOf("day").set({ hour: safeStartHour })}
            endTime={date.startOf("day").set({ hour: safeEndHour })}
            hourHeight={scaleConfig.layout.hourHeight}
            showLabels={false} // Keep minimal for week view
            closedOpacity={0.2}
            className="z-0"
          />

          {/* Current Time Line - only show if this day is today */}
          {showCurrentTimeLine && (
            <CurrentTimeLine
              startHour={safeStartHour}
              endHour={safeEndHour}
              hourHeight={scaleConfig.layout.hourHeight}
              showTimeLabel={dayIndex === 0}
            />
          )}
          {/* Appointments */}
          {dayAppointments.map(({ appointment, style, hasOverlap }) => (
            <CalendarAppointmentCard
              key={appointment.id}
              appointment={appointment}
              height={Number.parseInt(style.height?.toString().replace("px", "") || "24", 10)}
              pixelsPerHour={scaleConfig.layout.hourHeight}
              style={style}
              className={`${hasOverlap ? "shadow-lg border" : "shadow-sm"} transition-shadow hover:shadow-lg`}
              onClick={handleAppointmentClick}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Combined Headers and Content with synchronized horizontal scroll */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-max sm:min-w-full">
          {/* Week Headers - Sticky with responsive layout matching MultiColumnCalendarGrid */}
          <div className="sticky top-0 z-50 border border-border rounded-t-lg bg-card shadow-sm overflow-hidden bg-clip-padding">
            <div className="flex min-w-max sm:w-full">
              {/* Time Column Header - exact same as TimeColumn */}
              <div
                className={`${getTimeColumnWidth()} flex-shrink-0 h-12 border-r border-border bg-muted flex items-center justify-center`}
              >
                <span className="text-xs font-medium text-muted-foreground">Time</span>
              </div>

              {/* Content Columns Header - responsive: scroll on mobile, fit on desktop */}
              <div className="flex min-w-max sm:w-full">
                {weekDays.map((date, dayIndex) => (
                  <div
                    key={date.toISODate() ?? `day-${dayIndex}`}
                    className="flex-1 min-w-[280px] sm:min-w-0 border-r border-border last:border-r-0"
                  >
                    <div className="h-12 flex flex-col items-center justify-center bg-muted">
                      <div className="text-xs text-muted-foreground">{date.toFormat("ccc")}</div>
                      <div className="text-sm font-medium text-foreground">{date.day}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar Content */}
          <MultiColumnCalendarGrid
            timeConfig={timeConfig}
            numberOfColumns={7}
            containerClassName="border-0 border-l border-r border-b border-border rounded-b-lg"
            className=""
            timeColumnWidth={getTimeColumnWidth()}
            columnWidth="auto"
            minHeight="auto"
            enableHorizontalScroll={true}
            responsiveColumns={true}
          >
            {weekDays.map(renderDayColumn)}
          </MultiColumnCalendarGrid>
        </div>
      </div>
    </div>
  );
}
